<script setup lang="ts">
import {
  tpgIconClose,
  tpgIconFileDownload,
  tpgIconInfo,
  tpgIconOpenInNew,
} from '@tpg/grammar-web-icons';
import { computed, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarBadge,
  GrammarCard,
  GrammarQuaternaryButton,
  GrammarSectionHeader,
} from '@nora-monorepo/grammar-ui';
import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';
import {
  isImage,
  prettyPrintFileSize,
  truncateText,
} from '@nora-monorepo/ui-shared';

interface AttachmentGallerySideBarProps {
  attachment: MessageAttachmentDTO;
}

const props = defineProps<AttachmentGallerySideBarProps>();

defineEmits(['closeGalleryView']);

const { attachment } = toRefs(props);

const { d } = useI18n();
const isInfoBoxOpen = ref(false);
const showFullName = ref(false);

const mimeType = attachment.value.mimeType;
const fileName = attachment.value.fileName;
const attachmentSource = attachment.value.urls?.download?.url;
const imageDimensions = `${attachment.value.height?.toString()}x${
  props.attachment.width
} (px)`;
const downloadUrl = `${attachment.value.urls?.download?.url}&contentDisposition=inline`;

const displayName = computed(() => {
  return showFullName.value
    ? fileName
    : truncateText(fileName, 30);
});

const displayUploadedAt = computed(() => {
  if (!attachment.value.createdAt) {
    return null;
  }

  return d(attachment.value.createdAt, 'long');
});

function openInNewWindow() {
  if (downloadUrl) {
    window.open(downloadUrl, '_blank');
  }
}

function toggleInfoBox() {
  isInfoBoxOpen.value = !isInfoBoxOpen.value;
}

function toggleFullName() {
  showFullName.value = !showFullName.value;
}
</script>

<template>
  <div
    class="flex flex-col absolute bg-[#162936]/100 rounded-2xl top-10 right-10 z-9998 border border-[#263C4B]"
    @click.stop
  >
    <div class="p-1">
      <GrammarQuaternaryButton
        :icon-template="tpgIconOpenInNew"
        @click="openInNewWindow"
      />
      <a
        v-if="attachmentSource"
        :href="attachmentSource"
        :download="fileName"
      >
        <GrammarQuaternaryButton :icon-template="tpgIconFileDownload" />
      </a>
      <GrammarQuaternaryButton
        :icon-template="tpgIconInfo"
        @click="toggleInfoBox"
      />
      <GrammarQuaternaryButton
        :icon-template="tpgIconClose"
        @click="$emit('closeGalleryView')"
      />
    </div>

    <div
      v-if="isInfoBoxOpen"
      class="absolute top-full w-auto z-9999 right-0 pt-1"
    >
      <GrammarCard primary-label="Attachment info">
        <template #card-row-1>
          <div class="flex flex-col w-full pb-2 px-3">
            <div class="flex flex-row">
              <div class="cursor-pointer" @click="toggleFullName">
                <GrammarSectionHeader
                  class="text-[#8EA7B8]"
                  header-type="list-header"
                  label="File name"
                />

                <GrammarBadge
                  emphasis="no-background"
                  size="lg"
                  class="px-2"
                  :label="displayName"
                />
              </div>
            </div>

            <div v-if="displayUploadedAt">
              <GrammarSectionHeader
                class="text-[#8EA7B8]"
                header-type="list-header"
                label="Uploaded at"
              />
              <GrammarBadge
                class="text-[#8EA7B8] px-2"
                emphasis="no-background"
                :label="displayUploadedAt"
                size="lg"
              />
            </div>
            <div>
              <GrammarSectionHeader
                class="text-[#8EA7B8]"
                header-type="list-header"
                label="File size"
              />
              <GrammarBadge
                class="text-[#8EA7B8] px-2"
                emphasis="no-background"
                :label="prettyPrintFileSize(attachment.size)"
                size="lg"
              />
            </div>

            <div>
              <GrammarSectionHeader
                class="text-[#8EA7B8]"
                header-type="list-header"
                label="File format"
              />
              <GrammarBadge
                class="text-[#8EA7B8] px-2"
                emphasis="no-background"
                :label="attachment.mimeType"
                size="lg"
              />
            </div>

            <div v-if="isImage(mimeType)">
              <GrammarSectionHeader
                class="text-[#8EA7B8]"
                header-type="list-header"
                label="Dimensions"
              />
              <GrammarBadge
                class="text-[#8EA7B8] px-2"
                emphasis="no-background"
                :label="imageDimensions"
                size="lg"
              />
            </div>
          </div>
        </template>
      </GrammarCard>
    </div>
  </div>
</template>
